import { useEffect } from "react";
import { useCartData } from "@/zustand/cartStore";
import {
  SECTION_STEPS,
  isPostHogEnabled,
  isSectionComplete,
  trackCheckoutPageView,
  trackCheckoutStep,
} from "@/utils/posthog";

// Let the field's last change reach the form state before checking the section.
const SECTION_CHECK_DELAY = 100;

/**
 * PostHog checkout_page_view and the form-section steps (DEV-6713).
 */
export const usePostHogCheckout = () => {
  const hasSelectedProduct = useCartData((state) => Boolean(state.selectedProduct));

  // Product data arrives over REST; the page view waits for the first selected package.
  useEffect(() => {
    if (hasSelectedProduct) {
      trackCheckoutPageView();
    }
  }, [hasSelectedProduct]);

  // Nothing is attached while PostHog is off for the site.
  useEffect(() => {
    if (!isPostHogEnabled()) {
      return undefined;
    }

    const timers = [];

    const onFocusOut = (event) => {
      const section = event.target?.closest?.(
        Object.keys(SECTION_STEPS)
          .map((id) => `#${id}`)
          .join(",")
      );

      // Still inside the section: the visitor moved to its next field.
      if (!section || (event.relatedTarget && section.contains(event.relatedTarget))) {
        return;
      }

      timers.push(
        setTimeout(() => {
          const stepName = SECTION_STEPS[section.id];

          isSectionComplete(stepName).then((complete) => complete && trackCheckoutStep(stepName));
        }, SECTION_CHECK_DELAY)
      );
    };

    document.addEventListener("focusout", onFocusOut);

    return () => {
      document.removeEventListener("focusout", onFocusOut);
      timers.forEach(clearTimeout);
    };
  }, []);
};
